import React, { useState, useEffect, useRef } from 'react';
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, convertToRaw, ContentState, Modifier } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import { Typography, Box, Snackbar, Alert } from '@mui/material';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';

const InputTextProgram = ({ textProgram, setTextProgram }) => {
  const [editorState, setEditorState] = useState(() => EditorState.createEmpty());
  const [showWarning, setShowWarning] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const lastValidState = useRef(null);
  const isInitialized = useRef(false);

  // Подсчет символов в HTML
  const countHtmlChars = (html) => {
    const textWithoutTags = html.replace(/<[^>]*>/g, '');
    return textWithoutTags.length;
  };

  // Очистка HTML от проблемных сущностей
  const cleanHtml = (html) => {
    if (!html) return '';
    let cleaned = html.replace(/data-offset-key="[^"]*"/g, '');
    cleaned = cleaned.replace(/data-block="true"/g, '');
    cleaned = cleaned.replace(/data-editor="[^"]*"/g, '');
    // cleaned = cleaned.replace(/<a\s+[^>]*href="[^"]*"[^>]*>(.*?)<\/a>/gi, '$1');
    cleaned = cleaned.replace(/\s+/g, ' ').trim();
    return cleaned;
  };

  // Инициализация редактора из props
  useEffect(() => {
    if (textProgram && textProgram !== '<p></p>' && textProgram !== '' && !isInitialized.current) {
      try {
        const cleanedHtml = cleanHtml(textProgram);
        const contentBlock = htmlToDraft(cleanedHtml);
        if (contentBlock && contentBlock.contentBlocks) {
          const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
          const newState = EditorState.createWithContent(contentState);
          setEditorState(newState);
          lastValidState.current = newState;
          const count = countHtmlChars(draftToHtml(convertToRaw(newState.getCurrentContent())));
          setCharCount(count);
          isInitialized.current = true;
        }
      } catch (error) {
        console.error('Error initializing editor:', error);
        setEditorState(EditorState.createEmpty());
      }
    }
  }, [textProgram]);

  // Обновление счетчика при изменении editorState
  useEffect(() => {
    if (editorState) {
      const html = draftToHtml(convertToRaw(editorState.getCurrentContent()));
      const cleanHtmlString = cleanHtml(html);
      const charLength = countHtmlChars(cleanHtmlString);
      setCharCount(charLength);
    }
  }, [editorState]);

  // Обработка вставки текста
  const handlePastedText = (text, html, editorState) => {
    const cleanText = text.replace(/[^\w\s\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\n\r.,!?\-:;()"']/gu, '');
    
    const currentContent = editorState.getCurrentContent();
    const selection = editorState.getSelection();
    const currentHtml = draftToHtml(convertToRaw(currentContent));
    const currentLength = countHtmlChars(currentHtml);
    
    if (currentLength + cleanText.length <= 1250) {
      const newContentState = Modifier.replaceText(
        currentContent,
        selection,
        cleanText
      );
      const newEditorState = EditorState.push(editorState, newContentState, 'insert-characters');
      return newEditorState;
    } else {
      setShowWarning(true);
      return 'handled';
    }
  };

  const handleEditorChange = (state) => {
    try {
      const html = draftToHtml(convertToRaw(state.getCurrentContent()));
      const cleanHtmlString = cleanHtml(html);
      const charLength = countHtmlChars(cleanHtmlString);
      
      if (charLength <= 1250) {
        setEditorState(state);
        setTextProgram(cleanHtmlString);
        lastValidState.current = state;
      } else {
        setShowWarning(true);
        if (lastValidState.current) {
          setEditorState(lastValidState.current);
        }
      }
    } catch (error) {
      console.error('Error in handleEditorChange:', error);
      if (lastValidState.current) {
        setEditorState(lastValidState.current);
      }
    }
  };

  // Все смайлики
  const allEmojis = [
    '😊', '🙂', '😄', '😃', '😁', '🤔', '🧠', '💡', '🧐', '📚', '🎓',
    '✅', '⭐', '👍', '👏', '🔥', '🏆', '🏅', '🤝', '🙏', '🆘', '❓', 'ℹ️', '⚠️',
    '👀', '🔍', '🧮', '🔬', '🧪', '⚛️', '💻', '✍️', '📖', '📄', '📁',
    '📅', '⏰', '⏳', '📧', '💬', '🗣️', '📢', '0️⃣', '1️⃣', '2️⃣', '3️⃣', '4️⃣', '5️⃣', '🔟',
    '☀️', '🌟', '🚀', '🌱', '🌳', '👥', '👫', '✔️', '❌', '🎉', '💫', '🤖', '📢'
  ];

  return (
    <>
      <Typography variant="h6" gutterBottom>
        Введите аннотацию программы (не более 1250 символов)*:
      </Typography>
      
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Typography variant="body2" color={charCount > 1250 ? 'error' : charCount > 1100 ? 'warning.main' : 'text.secondary'}>
          {charCount} / 1250 символов
        </Typography>
      </Box>
      
      <Snackbar 
        open={showWarning} 
        autoHideDuration={3000} 
        onClose={() => setShowWarning(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="warning" onClose={() => setShowWarning(false)}>
          ⚠️ Превышен лимит в 1250 символов
        </Alert>
      </Snackbar>
      
      <Box sx={{ 
        border: '1px solid #ddd', 
        borderRadius: 1, 
        p: 1,
        '& .rdw-editor-main': {
          minHeight: '200px',
          maxHeight: '400px',
          overflow: 'auto'
        }
      }}>
        <Editor
          editorState={editorState}
          onEditorStateChange={handleEditorChange}
          handlePastedText={handlePastedText}
          toolbar={{
            options: ['inline', 'blockType', 'list', 'link', 'emoji', 'history'],
            emoji: {
              emojis: allEmojis
            },
            inline: {
              options: ['bold', 'italic', 'underline'],
            },
            blockType: {
              options: ['Normal', 'H1', 'H2', 'H3'],
            },
            list: {
              options: ['unordered', 'ordered'],
            },
          }}
          placeholder="Начните вводить аннотацию..."
          editorStyle={{ minHeight: '200px' }}
          stripPastedStyles={true}
        />
      </Box>
      
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        💡 Подсказка: для вставки смайликов нажмите на иконку 😊 в панели инструментов
      </Typography>
    </>
  );
};

export default InputTextProgram;